import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { string, object, boolean } from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import Markdown from "react-markdown";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";

import Send from "@mui/icons-material/Send";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { TLocation, TReactHookForm, TVehicle } from "types";

import { CookieStore } from "stores";

import {
  ReactHookFormIntPhone,
  ReactHookFormSelect,
  ReactHookFormSwitch,
  ReactHookFormText,
  ReactHookFormCheckbox,
} from "components";

enum ERateType {
  cashPurchase = "CASHPURCHASE",
  credit = "CREDIT",
  balloon = "BALLOON",
  leasing = "LEASING",
}
type TRateType = keyof typeof ERateType;

enum ETopic {
  vehicle = "VEHICLE",
  model = "MODEL",
  notSpecific = "NOTSPECIFIC",
}
type TTopic = keyof typeof ETopic;

interface IContact {
  contact?: {
    name?: string;
    byMail?: boolean;
    email?: string;
    byPhone?: boolean;
    phone?: string;
    getOffer?: boolean;
    finance?: { rateType?: TRateType; topic?: TTopic };
    testdrive?: boolean;
    message?: string;
    agreement?: boolean;
  };
  vehicle: TVehicle;
  configuration?: any;
  paperProps?: any;
  locationId?: number;
  setLocationHandler?: any;
}

const Contact: React.FC<IContact> = ({
  contact,
  vehicle,
  configuration = {
    insertLeadEmail: true,
    inquiry: {
      email: true,
      phone: true,
    },
  },
  paperProps = { sx: { p: 2 } },
  locationId,
  setLocationHandler,
}) => {
  const { state: cookies } = React.useContext(CookieStore);

  const {
    register,
    control,
    formState: { errors, isValid },
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      name: "",
      byMail: !!configuration.inquiry?.email,
      email: "",
      byPhone: !!configuration.inquiry?.phone,
      phone: "",
      getOffer: false,
      finance: { rateType: "cashPurchase", topic: "vehicle" },
      testdrive: false,
      message: "",
      agreement: false,
      location: vehicle.location
        ? vehicle.location.id
        : locationId || undefined,
      ...contact,
    },
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const { formatMessage: t } = useIntl();

  React.useEffect(() => {
    trigger();
  }, [trigger]);

  React.useEffect(() => {
    if (setLocationHandler) setValue("location", locationId);
  }, [locationId]);

  const submit = async () => {
    const url = `https://service.carbando.eu/contact/inquiry`;
    const payload = getValues();
    const shopId = configuration.showroomId || process.env.REACT_APP_SHOP_ID;
    const { wgnr } = vehicle;
    const rateType = t({
      id: `forms.contact.finance.rateType.options.${payload.finance.rateType}`,
    });
    const topic = t({
      id: `forms.contact.finance.topic.options.${payload.finance.topic}`,
    });
    const dealerId = payload.location
      ? t({
          id: `location.${payload.location}.standort_code`,
          defaultMessage: "undefined",
        })
      : null;

    const locations = [
      vehicle.location || undefined,
      ...(vehicle.locations || []),
    ];

    let leadEmail: string | undefined = undefined;
    if (configuration.insertLeadEmail) {
      leadEmail = payload.location
        ? locations.find((e) => e?.id === payload.location)?.email
        : undefined;
    }

    const body = {
      ...payload,
      rateType,
      topic,
      shopId,
      dealerId: dealerId === "undefined" ? null : dealerId,
      vehicleId: wgnr,
      campaign: "",
      campaignDetails: "",
      leadEmail,
    };
    if (cookies.utm_campaign) {
      body.campaign = "google-Ads";
      body.campaignDetails = cookies.utm_campaign;
    }

    const options = {
      method: "POST",
      body: JSON.stringify(body),
    };

    setIsLoading(true);
    const response = await fetch(url, options);
    setIsLoading(false);
    setIsLoaded(true);
    if (response.ok) {
      const result = await response.json();
    } else {
      alert("HTTP-Error: " + response.status);
    }
  };

  const locationChange = watch("location");
  React.useEffect(() => {
    if (setLocationHandler) {
      if (locationChange !== locationId) setLocationHandler(locationChange);
    }
  }, [locationChange]);

  const watchedValues = {
    byMail: watch("byMail"),
    byPhone: watch("byPhone"),
    getOffer: watch("getOffer"),
    testdrive: watch("testdrive"),
    message: watch("message"),
    name: watch("name"),
    agreement: watch("agreement"),
    location: watch("location"),
  };

  const step_2_isValid = () => {
    console.log("step_2_isValid", watchedValues.byMail, watchedValues.byPhone);
    return watchedValues.byMail || watchedValues.byPhone;
  };

  const step_3_isValid = () => {
    return (
      (watchedValues.testdrive ||
        watchedValues.getOffer ||
        watchedValues.message.length > 0) &&
      !!watchedValues.location
    );
  };

  const formIsValid = () => {
    return step_2_isValid() && step_3_isValid() && isValid;
  };

  return (
    <Paper
      id="Offer"
      data-testid="pages-detail-component-contact"
      variant="outlined"
      {...paperProps}
    >
      <Typography
        variant="h5"
        component="h2"
        sx={{ mb: 2 }}
        data-testid="typography-title"
      >
        <FormattedMessage
          id="pages.detail.offer.title"
          defaultMessage="Fahrzeug anfragen"
        />
      </Typography>
      <ContactForm
        {...{
          register,
          control,
          errors,
          setValue,
          trigger,
        }}
        {...watchedValues}
        errors={errors}
        disabled={isLoading || isLoaded}
        configuration={configuration}
        locations={vehicle.locations || []}
        locationId={locationId}
      />
      <Step
        color={watchedValues.agreement ? "darkgreen" : "darkred"}
        step={4}
        title="Absenden"
      >
        <Grid container sx={{ mt: 2 }}>
          <Grid
            size={{ xs: 12 }}
            sx={{
              p: { xs: 1, md: 2 },
              a: {
                color: "black",
                textDecoration: "none",
                fontWeight: "bold",
              },
              "a:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <ReactHookFormCheckbox
              control={control}
              name="agreement"
              disabled={isLoading || isLoaded}
              label={
                <Markdown>
                  {t(
                    {
                      id: "pages.detail.offer.agreement",
                      defaultMessage:
                        'Ich stimme den [allgemeinen Geschäftsbedingungen](https://cdn.carbando.eu/{customer}/AGB.pdf "allgemeinen Geschäftsbedingungen") und der [Datenschutzerklärung](https://cdn.carbando.eu/{customer}/Datenschutz.pdf "Datenschutzerklärung") zu und erkläre mich einverstanden, mit der Verarbeitung, Nutzung und der Weiterleitung meiner Daten zum Zwecke der Kontaktaufnahme. *',
                    },
                    { customer: process.env.REACT_APP_SHOP_ID || "carbando" },
                  )}
                </Markdown>
              }
            />
          </Grid>
          <Grid
            size={{ md: 6, xs: 12 }}
            sx={{ textAlign: "right", display: "grid", alignItems: "center" }}
          >
            <Stack direction="row" spacing={3} sx={{ justifySelf: "center" }}>
              <Avatar
                sx={{
                  backgroundColor: !watchedValues.name.length
                    ? "darkred"
                    : "darkgreen",
                }}
              >
                1
              </Avatar>
              <Avatar
                sx={{
                  backgroundColor: !(
                    watchedValues.byMail || watchedValues.byPhone
                  )
                    ? "darkred"
                    : (watchedValues.byMail && errors?.email) ||
                        (watchedValues.byPhone && errors?.phone)
                      ? "darkred"
                      : "darkgreen",
                }}
              >
                2
              </Avatar>
              <Avatar
                sx={{
                  backgroundColor: !(
                    (watchedValues.testdrive ||
                      watchedValues.getOffer ||
                      watchedValues.message.length) &&
                    watchedValues.location
                  )
                    ? "darkred"
                    : "darkgreen",
                }}
              >
                3
              </Avatar>
              <Avatar
                sx={{
                  backgroundColor: watchedValues.agreement
                    ? "darkgreen"
                    : "darkred",
                }}
              >
                4
              </Avatar>
            </Stack>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} sx={{ textAlign: "center" }}>
            <Button
              data-testid="button-submit"
              disabled={!formIsValid() || isLoading || isLoaded}
              color={isLoaded ? "success" : "primary"}
              variant="contained"
              sx={{ mt: 2, pr: 5 }}
              onClick={() => submit()}
            >
              {!(isLoading || isLoaded) && (
                <>
                  <FormattedMessage
                    id="pages.detail.offer.submit.isValid"
                    defaultMessage="Absenden"
                  />
                  <Send
                    fontSize="small"
                    sx={{ color: "white", position: "absolute", right: 10 }}
                  />
                </>
              )}
              {isLoading && (
                <>
                  <FormattedMessage
                    id="pages.detail.offer.submit.isLoading"
                    defaultMessage="Senden..."
                  />
                  <CircularProgress
                    color="inherit"
                    size={23}
                    sx={{ color: "white", position: "absolute", right: 10 }}
                  />
                </>
              )}
              {isLoaded && (
                <>
                  <FormattedMessage
                    id="pages.detail.offer.submit.isLoaded"
                    defaultMessage="Gesendet"
                  />
                  <ThumbUpIcon
                    fontSize="small"
                    sx={{ color: "darkgreen", position: "absolute", right: 10 }}
                  />
                </>
              )}
            </Button>
          </Grid>
        </Grid>
      </Step>
    </Paper>
  );
};

interface IContactForm extends TReactHookForm {
  byMail: boolean;
  byPhone: boolean;
  testdrive: boolean;
  getOffer: boolean;
  message: string;
  name: string;
  disabled?: boolean;
  configuration?: any;
  trigger?: any;
  location: any;
  locationId?: number;
  locations: TLocation[];
}

interface IStep {
  step: number;
  title: string;
  color?: string;
  sx?: any;
}
const Step: React.FC<IStep> = ({
  step = 1,
  sx = {},
  title = "",
  children,
  color = "grey",
}) => (
  <Paper
    sx={{ p: 2, mt: 1, ...sx }}
    variant="outlined"
    data-testid={`step-${step}`}
  >
    <Stack direction="row" spacing={2}>
      <Avatar
        variant="rounded"
        sx={{
          backgroundColor: color,
          fontFamily: "monospace",
          width: "2rem",
          height: "2rem",
        }}
      >
        {step}
      </Avatar>
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
    </Stack>
    <Divider sx={{ my: 2 }} />
    {children}
  </Paper>
);

const ContactForm = ({
  register,
  control,
  errors,
  setValue = () => null,
  byMail,
  byPhone,
  testdrive,
  getOffer,
  message,
  name,
  disabled = false,
  location,
  locationId,
  locations = [],
  configuration = {},
  trigger = () => null,
}: IContactForm): JSX.Element => {
  const activate = (which: string) => {
    setValue(which, true);
    const targets: Record<string, string> = {
      byMail: "email",
      byPhone: "phone",
    };
    trigger(targets[which]);
  };

  return (
    <form noValidate autoComplete="off">
      <input
        type="hidden"
        name="byPhone"
        value={byPhone}
        {...register("byPhone")}
      />
      <input
        type="hidden"
        name="byMail"
        value={byMail}
        {...register("byMail")}
      />
      <input
        type="hidden"
        name="getOffer"
        value={getOffer}
        {...register("getOffer")}
      />

      <Step
        color={name.length ? "darkgreen" : "darkred"}
        step={1}
        title="Bitte nennen Sie Ihren Namen"
      >
        <ReactHookFormText
          register={register}
          name="name"
          label="Name"
          required={true}
          error={!!errors?.name}
          props={{ fullWidth: true }}
          disabled={disabled}
        />
      </Step>
      <Step
        step={2}
        color={
          (!byMail && !byPhone) ||
          (byMail && errors?.email) ||
          (byPhone && errors?.phone)
            ? "darkred"
            : "darkgreen"
        }
        title="Bitte übermitteln Sie uns Ihre Kontaktdaten."
      >
        <Box sx={{ position: "relative" }}>
          {configuration.inquiry?.email === false &&
            configuration.inquiry?.phone === false && (
              <Typography variant="body1" gutterBottom>
                Bitte nennen Sie uns einen Kontaktweg und tragen entweder Ihre
                Telefonnummer, Ihre E-Mail-Adresse oder beides ein.
              </Typography>
            )}
          <Grid container>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormSwitch
                control={control}
                name="byMail"
                label="Bitte kontaktieren Sie mich per E-Mail"
                disabled={configuration.inquiry?.email || disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormText
                register={register}
                name="email"
                label="E-Mail"
                required={configuration.inquiry?.email}
                disabled={!byMail || disabled}
                type="email"
                error={byMail && errors?.email}
                props={{
                  fullWidth: true,
                }}
              />
            </Grid>
          </Grid>
          {!byMail && <Trigger onClick={() => activate("byMail")} />}
        </Box>
        <Box sx={{ my: 3, position: "relative" }}>
          <Grid container>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormSwitch
                control={control}
                name="byPhone"
                label="Bitte rufen Sie mich zurück"
                disabled={configuration.inquiry?.phone || disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormIntPhone
                control={control}
                name="phone"
                required={configuration.inquiry?.phone}
                countryDisabled
                error={byPhone && errors?.phone}
                disabled={!byPhone || disabled}
              />
            </Grid>
          </Grid>
          {!byPhone && <Trigger onClick={() => activate("byPhone")} />}
        </Box>
      </Step>
      <Step
        color={
          !((testdrive || getOffer || message.length) && location)
            ? "darkred"
            : "darkgreen"
        }
        step={3}
        title="Was können wir für sie tun?"
      >
        {locations.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <ReactHookFormSelect
              control={control}
              name="location"
              sort={false}
              defaultValue={locationId}
              options={locations.map((e) => e.id + "")}
              labelPrefix="contact.location"
              description="Dieses Fahrzeug ist an mehreren Standorten verfügbar. Bitte wählen Sie einen bevorzugten aus."
            />
          </Box>
        )}
        <Box sx={{ mb: 3, position: "relative" }}>
          <ReactHookFormSwitch
            control={control}
            name="testdrive"
            label="Ich interessiere ich für eine Probefahrt"
            disabled={disabled}
          />
          {!testdrive && <Trigger onClick={() => activate("testdrive")} />}
        </Box>
        <Box sx={{ mb: 2, position: "relative" }}>
          <Grid container>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: 2 }}>
              <ReactHookFormSwitch
                control={control}
                name="getOffer"
                label="Bitte unterbreiten Sie mir ein unverbindliches Angebot"
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Grid container>
                <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                  <ReactHookFormSelect
                    disabled={!getOffer || disabled}
                    control={control}
                    prefix="finance."
                    name="rateType"
                    sort={false}
                    defaultValue="leasingResidualValue"
                    options={["cashPurchase", "credit", "balloon", "leasing"]}
                    labelPrefix="contact.finance.rateType"
                    error={errors?.finance?.rateType.type}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <ReactHookFormSelect
                    disabled={!getOffer || disabled}
                    control={control}
                    prefix="finance."
                    name="topic"
                    sort={false}
                    defaultValue="vehicle"
                    options={["vehicle", "model", "notSpecific"]}
                    labelPrefix="contact.finance.topic"
                    error={errors?.finance?.topic.type}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {!getOffer && <Trigger onClick={() => activate("getOffer")} />}
        </Box>
        <ReactHookFormText
          register={register}
          name="message"
          label="Nachricht"
          required={false}
          props={{
            fullWidth: true,
            multiline: true,
            rows: 4,
          }}
          disabled={disabled}
        />
      </Step>
    </form>
  );
};

interface ITrigger {
  onClick: any;
}
const Trigger: React.FC<ITrigger> = ({ onClick }): JSX.Element => (
  <Box
    onClick={() => onClick()}
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "red",
      opacity: 0,
    }}
  />
);

const ValidationSchema = object().shape({
  name: string().required(),
  email: string().when("byMail", {
    is: true,
    otherwise: (schema) => schema,
    then: (schema) => string().email().required(),
  }),
  phone: string().when("byPhone", {
    is: true,
    otherwise: (schema) => schema,
    then: (schema) =>
      string()
        .trim()
        .matches(
          /^\+491(51|60|70|71|75|52|62|72|73|74|57|63|77|78|59|76|79)\d{7}\d*$/,
        )
        .min(1)
        .required(),
  }),
  // oneOfContacts: string().when(["byMail", "byPhone"], {
  //   is: (byMail: boolean, byPhone: boolean) => byMail || byPhone,
  //   then: (schema) => schema,
  //   otherwise: (schema) => string().trim().min(1),
  // }),

  byMail: boolean(),
  byPhone: boolean(),
  testDrive: boolean(),
  getOffer: boolean(),
  agreement: boolean().oneOf([true], "Field must be checked"),
});

export default Contact;
